import React, { useEffect, useRef, useState } from 'react';
import './Hero.css';
import { trackEvent } from '../analytics';

// Live-feeling counters for the floating "digital twin" card — purely
// decorative, but it's a direct visual echo of the What-If Scenario /
// growth-stage readouts from the pitch materials, so it doubles as an
// honest preview of what the real product's dashboard looks like.
function useTicker(target, duration = 2400) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setValue(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return value;
}

export default function Hero() {
  const videoRef = useRef(null);
  const day = useTicker(18);
  const yieldPct = useTicker(85);

  useEffect(() => {
    // Some mobile browsers need an explicit play() nudge even with autoPlay.
    const v = videoRef.current;
    if (v) {
      const playPromise = v.play();
      if (playPromise !== undefined) playPromise.catch(() => {});
    }
  }, []);

  return (
    <section id="home" className="hero">
      <div className="hero__media">
        <video
          ref={videoRef}
          className="hero__video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/assets/images/pitch-poster.jpg"
        >
          {/*
            Drop your 29-second loop video here:
              public/videos/hero-loop.mp4
            (mp4/H.264 recommended for widest device support — also add a
            .webm alongside it if you have one, for smaller file size).
          */}
          <source src="/videos/hero-loop.mp4" type="video/mp4" />
          <source src="/videos/hero-loop.webm" type="video/webm" />
        </video>
        <div className="hero__scrim" />
      </div>

      <div className="hero__content container">
        <p className="hero__kicker">Immersive Agri Training · Real Knowledge · Real Impact</p>
        <h1 className="hero__title">
          Learn farming
          <br />
          <span className="hero__title-accent">before you farm.</span>
        </h1>
        <p className="hero__subtitle">
          Inivayal builds digital-twin simulations that let students, entrepreneurs and
          institutions practice real agricultural decisions — starting with mushroom
          cultivation — without risking a single crop.
        </p>

        <div className="hero__actions">
          <a
            href="#contact"
            className="btn btn-yellow"
            onClick={() => trackEvent('cta_click', { cta: 'hero_invest' })}
          >
            Talk to the Founders
          </a>
          <a
            href="#pitch-deck"
            className="btn btn-ghost"
            onClick={() => trackEvent('cta_click', { cta: 'hero_pitch_deck' })}
          >
            View Pitch Deck
          </a>
        </div>
      </div>

      <div className="hero__sim-card" aria-hidden="true">
        <div className="hero__sim-head">
          <span className="hero__sim-dot" />
          Mushroom Cultivation · Live Simulation
        </div>
        <div className="hero__sim-grid">
          <div>
            <span className="hero__sim-label">Growth Stage</span>
            <strong>Day {day}</strong>
          </div>
          <div>
            <span className="hero__sim-label">Harvest Ready</span>
            <strong>{yieldPct}%</strong>
          </div>
          <div>
            <span className="hero__sim-label">pH Level</span>
            <strong>6.2</strong>
          </div>
        </div>
        <div className="hero__sim-bar">
          <div className="hero__sim-bar-fill" style={{ width: `${yieldPct}%` }} />
        </div>
      </div>

      <a href="#platform" className="hero__scroll-cue" aria-label="Scroll to learn more">
        <span />
      </a>
    </section>
  );
}
